// Quiz Scoring and Insights Logic

export const calculateScore = (answers) => {
  let totalScore = 0;
  let maxScore = 0;

  // Score yes/no questions (Q1-Q10)
  const yesNoQuestions = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10'];
  const weights = {
    q1: 1, q2: 1.5, q3: 1.5, q4: 1, q5: 1.5,
    q6: 1, q7: 1, q8: 1, q9: 1.5, q10: 1
  };

  yesNoQuestions.forEach(qId => {
    const weight = weights[qId];
    maxScore += weight * 10;
    if (answers[qId] === true) {
      totalScore += weight * 10;
    }
  });

  // Bonus/penalty from qualification questions (Q11-Q14)
  // Q11: Current situation
  if (answers.q11 === 'burnout' || answers.q11 === 'stuck') {
    totalScore += 5; // Indicates clear need, bonus
  } else if (answers.q11 === 'growth') {
    totalScore += 10; // Strong position
  }

  // Q12: Desired outcome
  if (answers.q12 === 'exit_clean' || answers.q12 === 'partner_majority') {
    totalScore += 5; // Clear intent
  }

  // Q14: Revenue range (critical qualifier)
  const revenueScore = {
    'under_50k': -10,
    '50k_250k': 5,
    '250k_500k': 10,
    '500k_1m': 15,
    '1m_2m': 15,
    'over_2m': 5
  };
  totalScore += revenueScore[answers.q14] || 0;
  maxScore += 15; // Max revenue bonus

  // Normalize to 0-100
  const percentage = Math.round((totalScore / maxScore) * 100);
  return Math.min(100, Math.max(0, percentage));
};

export const generateInsights = (answers, score) => {
  const insights = [];

  // Insight 1: Operational Health
  const operationalYes = ['q1', 'q2', 'q5', 'q8', 'q9'].filter(q => answers[q] === true).length;
  if (operationalYes >= 4) {
    insights.push({
      title: 'Strong Operational Foundation',
      description: 'Your business has solid systems in place. You\'ve built processes that reduce owner dependency and create consistency. This foundation makes scaling or transitioning much easier.'
    });
  } else if (operationalYes >= 2) {
    insights.push({
      title: 'Operational Systems Need Attention',
      description: 'You have some processes in place, but key operational gaps are holding you back. Documenting workflows and building manager capacity would unlock significant growth potential.'
    });
  } else {
    insights.push({
      title: 'Operations Heavily Owner-Dependent',
      description: 'Your business runs on you, not systems. This is the #1 barrier to scaling or exiting. The good news: this is fixable with the right SOPs and team structure.'
    });
  }

  // Insight 2: Financial Clarity
  const financialYes = ['q3', 'q4', 'q6', 'q7'].filter(q => answers[q] === true).length;
  if (financialYes >= 3) {
    insights.push({
      title: 'Financial House in Order',
      description: 'You know your numbers. Clean books, tracked metrics, and predictable revenue make your business attractive to partners or buyers and position you for confident decision-making.'
    });
  } else if (financialYes >= 2) {
    insights.push({
      title: 'Financial Visibility Needs Improvement',
      description: 'You have some financial tracking, but gaps in CAC, margins, or consistency create risk. Tightening your financial discipline would improve both operations and valuation.'
    });
  } else {
    insights.push({
      title: 'Financial Blind Spots Are Risky',
      description: 'Operating without clear financial data is flying blind. Getting your books audit-ready and understanding unit economics should be priority #1 before any major moves.'
    });
  }

  // Insight 3: Owner Readiness & Path Forward
  const situation = answers.q11;
  const revenue = answers.q14;
  
  if (situation === 'burnout' && (revenue === '50k_250k' || revenue === '250k_500k' || revenue === '500k_1m' || revenue === '1m_2m')) {
    insights.push({
      title: 'You\'re in Our Wheelhouse',
      description: 'Burnout + revenue in our sweet spot means we can likely help. Whether it\'s a clean exit, turnaround partnership, or phased transition, let\'s have a conversation about paths forward.'
    });
  } else if (situation === 'stuck' || situation === 'growth') {
    insights.push({
      title: 'Fixable Problems with High Upside',
      description: 'Your challenges are operational, not fundamental. With the right systems, team structure, and execution, your business has strong growth potential. This is what we specialize in.'
    });
  } else if (situation === 'life_change') {
    insights.push({
      title: 'Life Transitions Require Clear Options',
      description: 'When personal circumstances force business decisions, having multiple paths forward is critical. Whether you need to exit, bring in help, or restructure, we can help you evaluate options.'
    });
  } else {
    insights.push({
      title: 'Exploration Phase',
      description: 'You\'re not in crisis, which gives you leverage. Use this time to understand what your business is worth, where systems need tightening, and what your options look like when you\'re ready.'
    });
  }

  return insights;
};

export const determineNextSteps = (answers, score) => {
  const revenue = answers.q14;
  const situation = answers.q11;
  const outcome = answers.q12;

  // Qualification logic
  const isQualifiedRevenue = ['50k_250k', '250k_500k', '500k_1m', '1m_2m'].includes(revenue);
  const isUrgentSituation = ['burnout', 'stuck', 'life_change'].includes(situation);
  const isClearIntent = ['exit_clean', 'partner_majority', 'partner_minority'].includes(outcome);

  // STRONG FOUNDATION (66+)
  if (score >= 66) {
    return {
      type: '1on1',
      category: 'Strong Foundation',
      pdfUrl: '/strong-foundation.pdf',
      pdfTitle: 'From Chaos to Cash-Out: Systemize and Sell Your Business Sooner',
      title: 'Strong Foundation - You\'re Ready for Next-Level Growth',
      description: 'Your business has solid operational systems in place. You\'re positioned well for scaling, partnership, or a strategic exit. Let\'s discuss how to maximize your business value.',
      resources: [
        'Download Your FREE Guide: "From Chaos to Cash-Out"',
        'Schedule: 1:1 Strategy Session with Mike Lucero',
        'Watch: How we evaluate and structure deals'
      ]
    };
  }

  // GROWTH POTENTIAL (41-65)
  if (score >= 41 && score <= 65) {
    return {
      type: 'group',
      category: 'Growth Potential',
      pdfUrl: '/growth-potential.pdf',
      pdfTitle: 'Escape the Grind: Your 30-Day Business Freedom Blueprint',
      title: 'Growth Potential - You\'re Close to Breaking Through',
      description: 'Your business has promise, but operational gaps are holding you back. With the right systems and support, you can unlock significant growth. Start with our proven frameworks.',
      resources: [
        'Download Your FREE Guide: "Escape the Grind: 30-Day Blueprint"',
        'Join: Business Systems Workshop',
        'Access: Monthly live Q&A with Mike Lucero'
      ]
    };
  }

  // BUILDING THE BASICS (0-40)
  return {
    type: 'content',
    category: 'Building the Basics',
    pdfUrl: '/building-basics.pdf',
    pdfTitle: 'Escape Daily Chaos: Proven Systems for Founder Freedom in 30 Days',
    title: 'Building the Basics - Start with Solid Foundations',
    description: 'Your business needs foundational systems before it\'s ready for partnership or scaling. That\'s completely normal. Start with our free resources to build operational clarity and consistency.',
    resources: [
      'Download Your FREE Guide: "Escape Daily Chaos"',
      'Subscribe: Weekly newsletter with actionable tips',
      'Watch: YouTube series on business fundamentals',
      'Join: Private community for business owners'
    ]
  };
};
